<script setup>
import { computed, ref, inject, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useActionCenterStore } from '../../stores/ActionCenterStore'
import { useNavigationStore } from '../../stores/NavigationStore'
import BaseBookOverviewTable from './BaseBookOverviewTable.vue'
import { formatRiskDollars, extractActiveEventsFromPlanYears, aggregateRequirementsFromFlatEvents, formatMMMDYYYY } from './helpers'
import { BOOK_VIEWS } from './constants'
import { getAccountOwners } from '../../utils/accountOwnersHelpers'

const props = defineProps({
  viewMode: {
    type: String,
    default: BOOK_VIEWS.ALL
  },
  searchTerm: {
    type: String,
    default: ''
  }
})

const actionCenterStore = useActionCenterStore()
const navigationStore = useNavigationStore()

const { filteredPlanYears, accountOwnerPlanYears } = storeToRefs(actionCenterStore)
const { customNavigation } = storeToRefs(navigationStore)

const emitter = inject('emitter', null)

// ===== ASSESSMENT STATUS =====
const STATUSES = {
  QUESTIONNAIRE_NOT_STARTED: 'Questionnaire not started',
  QUESTIONNAIRE_STARTED: 'Questionnaire started',
  QUESTIONNAIRE_FINISHED: 'Questionnaire finished',
  ASSESSMENT_STARTED: 'Assessment started',
  COMPLETE: 'Complete'
}

function getAssessmentStatus(py) {
  if (typeof py.assessment_status === 'number') {
    switch (py.assessment_status) {
      case 0: return STATUSES.QUESTIONNAIRE_NOT_STARTED
      case 1: return STATUSES.QUESTIONNAIRE_STARTED
      case 2: return STATUSES.QUESTIONNAIRE_FINISHED
      case 3: return STATUSES.ASSESSMENT_STARTED
      case 4: return STATUSES.COMPLETE
    }
  }
  return STATUSES.COMPLETE
}

// ===== VIEW CONFIGURATION =====
// Each view defines: filter, defaultSort, taskMode, taskActions, and optional summaryOverrides.
// taskActions[].isVisible(row) controls per-row visibility of each option.
// summaryOverrides lists which summary pill columns to show (others render empty).
const DROPDOWN_TASK_ACTIONS = [
  {
    key: 'send-calendar-reminders',
    label: 'Send Calendar Reminders',
    isVisible: (row) => row.upcomingCount > 0
  },
  // {
  //   key: 'send-action-item-nudge',
  //   label: 'Send Action Item Nudge',
  //   isVisible: (row) => row.actionItemsVisible && row.actionItemsCount > 0
  // },
  {
    key: 'complete-action-items',
    label: 'Complete Action Items',
    isVisible: (row) => row.hasCompleteAssessment && row.actionItemsCount > 0
  },
  {
    key: 'complete-questionnaire',
    label: 'Complete Questionnaire',
    isVisible: (row) => !row.hasCompleteAssessment
      && (row.assessmentStatus === STATUSES.QUESTIONNAIRE_NOT_STARTED
        || row.assessmentStatus === STATUSES.QUESTIONNAIRE_STARTED)
  },
  {
    key: 'complete-assessment',
    label: 'Complete Assessment',
    isVisible: (row) => !row.hasCompleteAssessment
      && (row.assessmentStatus === STATUSES.QUESTIONNAIRE_FINISHED
        || row.assessmentStatus === STATUSES.ASSESSMENT_STARTED)
  },
  {
    key: 'renew-employer',
    label: 'Renew Employer',
    isVisible: (row) => row.isPastPlanYear
  }
]

const NAVIGATE_TO_FORM_ACTION = [
  {
    key: 'navigate-to-form',
    label: 'Go to Form',
    isVisible: () => true
  }
]

const INCOMPLETE_ACTIONS_BUTTONS = [
  {
    key: 'send-action-item-nudge',
    label: 'Send Nudge',
    icon: 'bi-alarm-fill',
    variant: 'btn-outline-primary',
    isVisible: () => false
  },
  {
    key: 'complete-action-items',
    label: 'Mark Items Complete',
    icon: 'bi-check-circle-fill',
    variant: 'btn-primary',
    isVisible: (row) => row.hasCompleteAssessment
  }
]

const VIEW_CONFIGS = {
  [BOOK_VIEWS.ALL]: {
    filter: null,
    defaultSort: { key: 'riskPercent', asc: true },
    taskMode: 'dropdown',
    taskActions: DROPDOWN_TASK_ACTIONS
  },
  [BOOK_VIEWS.HIGH_RISK]: {
    filter: (row) => row.hasCompleteAssessment && row.riskPercent < 50,
    defaultSort: { key: 'riskPercent', asc: true },
    taskMode: 'dropdown',
    taskActions: DROPDOWN_TASK_ACTIONS
  },
  [BOOK_VIEWS.INCOMPLETE_ACTIONS]: {
    filter: (row) => row.hasCompleteAssessment && row.actionItemsCount > 0,
    defaultSort: { key: 'riskPercent', asc: true },
    taskMode: 'buttons',
    taskActions: INCOMPLETE_ACTIONS_BUTTONS,
    hiddenColumns: ['calendarRequirements', 'assessment']
  },
  [BOOK_VIEWS.INCOMPLETE_ASSESSMENTS]: {
    filter: (row) => !row.hasCompleteAssessment,
    defaultSort: { key: 'assessment', asc: true },
    taskMode: 'button',
    taskActions: NAVIGATE_TO_FORM_ACTION,
    summaryOverrides: ['employer', 'assessment'],
    maskedColumns: ['riskScore', 'riskDollars', 'actionItems', 'calendarRequirements']
  }
}

const activeViewConfig = computed(() => VIEW_CONFIGS[props.viewMode] || VIEW_CONFIGS[BOOK_VIEWS.ALL])

// Map employer_id → latest plan year (most recent plan_start_date) across ALL plan years
const latestPlanYearByEmployer = computed(() => {
  const map = {}
  const allPYs = accountOwnerPlanYears.value || []
  for (const py of allPYs) {
    const eid = py.employer_id
    if (!eid || !py.plan_start_date) continue
    const [m, d, y] = py.plan_start_date.split('/').map(Number)
    if (!m || !d || !y) continue
    const dateVal = new Date(y, m - 1, d).getTime()
    if (!map[eid] || dateVal > map[eid].dateVal) {
      map[eid] = { dateVal, planStartDate: py.plan_start_date, planYearId: py.id }
    }
  }
  return map
})

// ===== SORT STATE =====
const sortKey = ref(activeViewConfig.value.defaultSort.key)
const sortAsc = ref(activeViewConfig.value.defaultSort.asc)
const sortNullsFirst = ref(false)

watch(() => props.viewMode, () => {
  const defaults = activeViewConfig.value.defaultSort
  sortKey.value = defaults.key
  sortAsc.value = defaults.asc
  sortNullsFirst.value = false
})

function sortByCompletedField(a, b, field) {
  const aComplete = a.hasCompleteAssessment
  const bComplete = b.hasCompleteAssessment

  if (sortNullsFirst.value) {
    if (!aComplete && bComplete) return -1
    if (aComplete && !bComplete) return 1
  } else {
    if (aComplete && !bComplete) return -1
    if (!aComplete && bComplete) return 1
  }

  if (aComplete && bComplete) {
    const aVal = Number(a[field]) || 0
    const bVal = Number(b[field]) || 0
    return sortAsc.value ? aVal - bVal : bVal - aVal
  }

  const cmpFallback = a.employerName.localeCompare(b.employerName)
  return sortAsc.value ? cmpFallback : -cmpFallback
}

const allRows = computed(() => {
  const term = (props.searchTerm || '').trim().toLowerCase()

  const baseRows = filteredPlanYears.value.map(py => {
    const employerName = py.employer?.name || 'Unknown Employer'
    const employerId = py.employer_id
    const assessmentStatus = getAssessmentStatus(py)
    const hasCompleteAssessment = assessmentStatus === STATUSES.COMPLETE

    const riskPercent = Number(py.percentage_compliance_risk || 0)
    const riskDollars = Number(py.total_compliance_risk || 0)
    const actionItems = Array.isArray(py.action_items) ? py.action_items : []
    const actionItemsCount = actionItems.length

    // Risk labels only visible when assessment is complete (matching EmployerActionRiskData logic)
    const riskPercentLabel = riskPercent && hasCompleteAssessment ? `${Math.round(riskPercent)}%` : '--'
    const riskDollarsLabel = riskDollars && hasCompleteAssessment ? formatRiskDollars(riskDollars) : '--'

    // Action items only visible when assessment is complete
    const actionItemsVisible = hasCompleteAssessment
    const actionItemsLabel = actionItemsVisible
      ? `${actionItemsCount} Action Item${actionItemsCount !== 1 ? 's' : ''}`
      : '--'

    // Past plan year: plan_start_date + 1 year < today
    const planStartDate = py.plan_start_date || ''
    let isPastPlanYear = false
    if (planStartDate) {
      const [m, d, y] = planStartDate.split('/').map(Number)
      if (m && d && y) {
        const endDate = new Date(y + 1, m - 1, d)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        isPastPlanYear = today >= endDate
      }
    }

    // Calendar Requirements: count all active events within the next 30 days
    const now = new Date()
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
    const upcomingActiveEvents = extractActiveEventsFromPlanYears([py], { dateStart: now, dateEnd: thirtyDaysFromNow })
    const upcomingCount = upcomingActiveEvents.length
    const calendarRequirementsLabel = isPastPlanYear ? 'Not Applicable' : `${upcomingCount} Upcoming`

    const assessmentTooltip = (assessmentStatus === STATUSES.QUESTIONNAIRE_NOT_STARTED
      || assessmentStatus === STATUSES.QUESTIONNAIRE_STARTED)
      ? 'Go to Questionnaire'
      : (assessmentStatus === STATUSES.QUESTIONNAIRE_FINISHED
        || assessmentStatus === STATUSES.ASSESSMENT_STARTED)
        ? 'Go to Assessment'
        : null

    let riskColor = null
    if (hasCompleteAssessment) {
      riskColor = riskPercent >= 75 ? 'success'
        : riskPercent >= 50 ? 'warning'
        : 'danger'
    }

    return {
      id: py.id,
      employerId,
      employerName,
      riskPercent,
      riskDollars,
      riskPercentLabel,
      riskDollarsLabel,
      actionItemsCount,
      actionItemsVisible,
      actionItemsLabel,
      actionItems,
      upcomingActiveEvents,
      upcomingCount,
      calendarRequirementsLabel,
      assessmentStatus,
      assessmentTooltip,
      hasCompleteAssessment,
      riskColor,
      isPastPlanYear,
      planStartDate,
      rawPlanYear: py
    }
  })

  let filtered = term
    ? baseRows.filter(row =>
        row.employerName.toLowerCase().includes(term) ||
        row.assessmentStatus.toLowerCase().includes(term)
      )
    : baseRows

  // Apply view-specific filter
  const viewFilter = activeViewConfig.value.filter
  if (viewFilter) {
    filtered = filtered.filter(viewFilter)
  }

  const sorted = [...filtered].sort((a, b) => {
    if (sortKey.value === 'riskPercent') {
      return sortByCompletedField(a, b, 'riskPercent')
    }

    if (sortKey.value === 'riskDollars') {
      return sortByCompletedField(a, b, 'riskDollars')
    }

    if (sortKey.value === 'actionItems') {
      return sortByCompletedField(a, b, 'actionItemsCount')
    }

    if (sortKey.value === 'calendarRequirements') {
      const aVal = a.upcomingCount
      const bVal = b.upcomingCount
      return sortAsc.value ? aVal - bVal : bVal - aVal
    }

    let av, bv
    switch (sortKey.value) {
      case 'assessment':
        av = a.assessmentStatus
        bv = b.assessmentStatus
        break
      case 'employer':
      default:
        av = a.employerName
        bv = b.employerName
        break
    }

    if (typeof av === 'string' && typeof bv === 'string') {
      const cmp = av.localeCompare(bv)
      return sortAsc.value ? cmp : -cmp
    }

    const numA = Number(av) || 0
    const numB = Number(bv) || 0
    return sortAsc.value ? numA - numB : numB - numA
  })

  return sorted
})


const summaryStats = computed(() => {
  const rows = allRows.value
  const employerCount = rows.length

  // Risk Score avg: only rows with assessment complete (includes 0%)
  const completedRows = rows.filter(r => r.hasCompleteAssessment)
  const avgRisk = completedRows.length > 0
    ? Math.round(completedRows.reduce((sum, r) => sum + r.riskPercent, 0) / completedRows.length)
    : 0
  const avgRiskLabel = completedRows.length > 0 ? `${avgRisk}% Avg` : '--'
  const avgRiskColor = completedRows.length === 0 ? 'neutral'
    : avgRisk >= 75 ? 'success'
    : avgRisk >= 50 ? 'warning'
    : 'danger'

  // Risk $$: sum only from completed assessments
  const totalRiskDollars = completedRows.reduce((sum, r) => sum + r.riskDollars, 0)
  const totalRiskDollarsLabel = completedRows.length > 0 ? formatRiskDollars(totalRiskDollars) : '--'

  // Action Items: count rows that visibly have 1+ action items (only when assessment is complete)
  const actionItemsNeedAttention = rows.filter(r => r.actionItemsVisible && r.actionItemsCount > 0).length
  const actionItemsColor = actionItemsNeedAttention > 0 ? 'danger' : 'success'

  // Calendar Requirements: count employers that have pending (un-reminded) requirements
  const calendarReqNeedReminders = rows.filter(r => r.upcomingCount > 0).length
  const calendarReqColor = calendarReqNeedReminders > 0 ? 'warning' : 'success'

  // Assessment: count employers with incomplete assessments
  const assessmentsNeedCompletion = rows.filter(r => !r.hasCompleteAssessment).length
  const assessmentColor = assessmentsNeedCompletion > 0 ? 'danger' : 'success'

  return {
    employerCount,
    avgRiskLabel,
    avgRiskColor,
    totalRiskDollarsLabel,
    actionItemsNeedAttention,
    actionItemsColor,
    calendarReqNeedReminders,
    calendarReqColor,
    assessmentsNeedCompletion,
    assessmentColor
  }
})

function toggleSort(key) {
  if (sortKey.value === key) {
    sortAsc.value = !sortAsc.value
  } else {
    sortKey.value = key
    sortAsc.value = true
  }
  sortNullsFirst.value = false
}

// ===== TASK ACTION HANDLERS =====
const TASK_HANDLERS = {
  'send-calendar-reminders': (row) => {
    if (!emitter || !row.upcomingActiveEvents?.length) return

    const aggregatedRequirements = aggregateRequirementsFromFlatEvents(row.upcomingActiveEvents).map(group => ({
      ...group,
      earliestDueDateStr: group.earliestDueDateObj ? formatMMMDYYYY(group.earliestDueDateObj) : '',
      selected: true,
      reminderBusinessDays: null,
      reminderDate: null
    }))

    emitter.emit('action-center:start-send-reminders', {
      requirements: aggregatedRequirements,
      wasNext30DaysFilterActive: false
    })
  },

  'send-action-item-nudge': () => {
    // Placeholder: to be implemented later
  },

  'complete-action-items': (row) => {
    if (!row?.rawPlanYear) return

    if (row.hasCompleteAssessment && emitter) {
      emitter.emit('action-center:open-action-items-offcanvas', {
        employerName: row.employerName,
        riskPercentLabel: row.riskPercentLabel,
        riskDollarsLabel: row.riskDollarsLabel,
        requirementsCount: row.rawPlanYear.requirements_needing_attention || 0,
        actionItems: row.actionItems,
        planYearId: row.rawPlanYear.id || row.rawPlanYear.plan_year_id,
        employerId: row.employerId,
        contactEmails: row.rawPlanYear.employer?.contact_emails || ''
      })
    }
  },

  'complete-questionnaire': (row) => navigateToForm(row),
  'complete-assessment': (row) => navigateToForm(row),
  'navigate-to-form': (row) => navigateToForm(row),

  'renew-employer': (row) => {
    if (!emitter || !row?.rawPlanYear) return
    const employer = row.rawPlanYear.employer || {}
    const latest = latestPlanYearByEmployer.value[row.employerId]
    emitter.emit('open-renew-employer-modal', {
      id: employer.id || row.employerId,
      name: row.employerName,
      ein: employer.ein || '',
      contact_name: employer.contact_name || '',
      contact_emails: employer.contact_emails || '',
      plan_start_date: latest ? latest.planStartDate : row.planStartDate,
      currentPlanStartDate: row.planStartDate,
      planYearId: latest ? latest.planYearId : row.rawPlanYear.plan_year_id,
      accountOwners: getAccountOwners(employer)
    })
  }
}

function navigateToForm(row) {
  if (!row?.rawPlanYear) return
  const routes = customNavigation.value.routes
  const detailRoute = routes.DetailEmployer
  const formToLoad = (row.assessmentStatus === STATUSES.QUESTIONNAIRE_NOT_STARTED
    || row.assessmentStatus === STATUSES.QUESTIONNAIRE_STARTED)
    ? 1 : 2

  customNavigation.value.navigate({
    newPage: detailRoute.name,
    subPage: detailRoute.children[formToLoad].name,
    params: { planYearId: row.rawPlanYear.id || row.rawPlanYear.plan_year_id }
  })
}

function onTaskClicked(payload) {
  const { row, action } = payload
  const handler = TASK_HANDLERS[action]
  if (handler) handler(row)
}

async function scrollToEmployer({ planYearId }) {
  if (!planYearId) return

  const rowIndex = allRows.value.findIndex(row => row.id === planYearId)
  if (rowIndex === -1) return

  await nextTick()

  const element = document.getElementById(`allEmployerRow-${planYearId}`)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })

    element.classList.add('highlight-row')

    setTimeout(() => {
      element.classList.add('highlight-soft')
    }, 5000)

    setTimeout(() => {
      element.classList.add('highlight-fade')
      setTimeout(() => {
        element.classList.remove('highlight-row', 'highlight-soft', 'highlight-fade')
      }, 1000)
    }, 10000)
  }
}

onMounted(() => {
  if (emitter) {
    emitter.on('action-center:scroll-to-employer', scrollToEmployer)
  }
})

onUnmounted(() => {
  if (emitter) {
    emitter.off('action-center:scroll-to-employer', scrollToEmployer)
  }
})

defineExpose({ allRows })
</script>

<template>
  <div id="bookAllEmployersSection">
    <div class="w-100">
      <div class="card border-0">
        <div class="card-body py-0 px-0">
          <div class="table-responsive">
            <BaseBookOverviewTable
              :employers="allRows"
              :sort-key="sortKey"
              :sort-asc="sortAsc"
              :summary-stats="summaryStats"
              :task-actions="activeViewConfig.taskActions"
              :task-mode="activeViewConfig.taskMode"
              :summary-overrides="activeViewConfig.summaryOverrides || null"
              :masked-columns="activeViewConfig.maskedColumns || null"
              :hidden-columns="activeViewConfig.hiddenColumns || null"
              @toggle-sort="toggleSort"
              @task-clicked="onTaskClicked"
              @assessment-clicked="navigateToForm"
            />
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
.table td,
.table th,
.card-header {
  border: 1px solid #C5CDD4;
}

.table tr:first-child th {
  border-top: none;
}

</style>

<style>
#baseBookOverviewTableBody tr td {
  transition: background-color 1s ease-out, font-weight 0.3s ease-out;
}

#baseBookOverviewTableBody tr.highlight-row td {
  background-color: var(--green-060);
  font-weight: 700 !important;
}

#baseBookOverviewTableBody tr.highlight-row.highlight-soft td {
  background-color: #F0F7F4;
  font-weight: normal;
}

#baseBookOverviewTableBody tr.highlight-row.highlight-fade td {
  background-color: transparent;
  font-weight: normal;
}
</style>
